import React from 'react'
import { Button } from '@components/Button/Button'
import Footer from '@components/Footer'
import Header from '@components/Header'
import Page from '@components/Page'
import { setClinicId, setOriginalClinicId, useConfig, useConfigDispatch } from '@context/ConfigurationContext'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import routes from 'src/config/routes'
import { useCityClinics } from '@hooks/useCityClinics'
import useStyles from './styles'
import clsx from 'clsx'
import useWizardManager from '@hooks/useWizardManager'
import Spinner from '@components/Spinner'
import {
  resetSelectedBookable,
  setPreferredPractitioners,
  useChoiceDispatch,
  useChoiceState,
} from '@context/ChoiceContext'
import {
  clearPractitioners,
  resetSelectedPractitionersFor,
  usePractitionersDispatch,
} from '@context/PractitionersContext'
import captureExceptionWithResponse from 'src/services/sentry'
import { useDateFnsLocale } from 'src/react/hooks/useDateFnsLocale'
import { formatInTimeZone } from 'date-fns-tz'
import { TEST_IDS } from 'src/constants/testIds'
import { useSelectTreatmentForManyClinics } from '@hooks/useSelectTreatmentForManyClinics'
import { useIsAllClinicsVariant } from '@hooks/useIsAllClinicsVariant'
import { CategorizedTreatmentsWithPartialTreatments } from '@customTypes/CategorizedTreatment'
import { resetIsVerified, useVerifyPhoneContext, useVerifyPhoneDispatch } from '@context/VerifyPhoneContext'

interface Props {
  categorizedTreatments: CategorizedTreatmentsWithPartialTreatments[]
}

export const ClinicScreen = ({ categorizedTreatments }: Props) => {
  const { clinicId } = useConfig()
  const configDispatch = useConfigDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const classes = useStyles()
  const { data: clinics, isLoading } = useCityClinics()
  const { treatmentBundleTypeReferenceId } = useChoiceState()
  const choiceDispatch = useChoiceDispatch()
  const practitionersDispatch = usePractitionersDispatch()
  const verifyPhoneDispatch = useVerifyPhoneDispatch()
  const locale = useDateFnsLocale()
  const isAllClinicsVariant = useIsAllClinicsVariant()
  const { phone } = useVerifyPhoneContext()

  useWizardManager({
    targetRoute: routes.clinic,
  })

  const goToNextPage = () => {
    history.push(routes.patient)
  }

  const goBack = () => {
    if (isAllClinicsVariant) return history.push(routes.district)
    return history.push(routes.treatment)
  }

  const handleSelectTreatment = useSelectTreatmentForManyClinics(goToNextPage, categorizedTreatments || [])

  const getFlatTreatments = () => categorizedTreatments?.flatMap((category) => category.treatmentBundles)

  const getSelectedTreatment = () =>
    getFlatTreatments()?.find((treatment) => treatment.referenceId === treatmentBundleTypeReferenceId)

  const getTreatmentForClinic = (clinicId: string) => {
    const selectedTreatment = getSelectedTreatment()
    if (selectedTreatment?.clinicReferenceId === clinicId) {
      return selectedTreatment.referenceId!
    } else {
      const sameTreatmentInSelectedClinic = getFlatTreatments()
        ?.filter(
          (treatment) => treatment.treatmentPurposeReferenceId === selectedTreatment?.treatmentPurposeReferenceId
        )
        .find((treatment) => treatment.clinicReferenceId === clinicId)?.referenceId

      if (!sameTreatmentInSelectedClinic) {
        captureExceptionWithResponse(
          `Treatment ${treatmentBundleTypeReferenceId} not found for selected clinic: ${clinicId}`,
          { treatmentBundleTypeReferenceId, categorizedTreatments, clinicId }
        )

        return treatmentBundleTypeReferenceId
      }

      return sameTreatmentInSelectedClinic
    }
  }

  const selectClinic = (referenceId: string) => {
    if (referenceId !== clinicId) {
      practitionersDispatch(clearPractitioners())
      practitionersDispatch(resetSelectedPractitionersFor())
      choiceDispatch(resetSelectedBookable())
      choiceDispatch(setPreferredPractitioners([]))
      if (phone) verifyPhoneDispatch(resetIsVerified(phone))
    }

    configDispatch(setClinicId(referenceId))
    configDispatch(setOriginalClinicId(referenceId))
    handleSelectTreatment(getTreatmentForClinic(referenceId))
  }

  return (
    <Page
      header={<Header desktopHeadingText={t('clinic_page_title')} mobileHeadingText="" onBackClick={goBack} />}
      body={
        <div className={classes.container}>
          {isLoading && <Spinner size="large" center />}
          {clinics?.map((clinic) => (
            <div
              className={clsx(classes.clinicContainer, { [classes.selected]: clinic.referenceId === clinicId })}
              onClick={() => selectClinic(clinic.referenceId)}
              key={clinic.referenceId}
              data-test={TEST_IDS.clinicContainer}
            >
              <div className={classes.clinicInfoContainer}>
                <span className={classes.clinicName}>{clinic.officialName}</span>
                <span className={classes.clinicAdress}>
                  <span>
                    {clinic.street} {clinic.houseNumber}
                  </span>
                  {clinic.distanceInKm && (
                    <>
                      {' '}
                      | <span className={classes.distanceToClinic}>{clinic.distanceInKm}km</span>
                    </>
                  )}
                </span>
              </div>
              {clinic.nextAvailableAppointment?.start && (
                <>
                  <div className={classes.separator} />
                  <div className={classes.nextAvailableAppointment}>
                    <span className={classes.appointmentStartLabel}>{t('next_available_appointment')}</span>
                    <span className={classes.appointmentStart}>
                      {formatInTimeZone(
                        new Date(clinic.nextAvailableAppointment.start),
                        'Europe/Berlin',
                        'dd LLL | HH:mm',
                        { locale }
                      )}
                    </span>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      }
      footer={
        <Footer
          button={
            clinicId ? (
              <Button fullSize type="button" onClick={goToNextPage} data-test={TEST_IDS.nextButton} text={t('next')} />
            ) : null
          }
        />
      }
    />
  )
}
